import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { analytics, monitoring } from 'common/services';
import { isE2ESession, isProduction, isStaffEmail } from 'common/utils';
import { addAppSuccessMessage } from 'modules/alerts/state';
import { isApiResponse } from 'modules/api/utils';
import { removeExpiredKeptCopies, removeKeptCopiesNotOwnedBy } from 'modules/keycloak/forcedLogout';
import { AppRoutes } from 'modules/router';
import { push } from 'redux-first-history';
import { handleQueryError } from 'store/utils';

import { userApi } from './userApi';

import type { Effect, RootState } from 'store';

export const userApiListener = createListenerMiddleware<RootState>();

userApiListener.startListening({
  matcher: isAnyOf(
    userApi.endpoints.getAccount.matchRejected,
    userApi.endpoints.updateAccount.matchRejected,
    userApi.endpoints.updateAccountAvatar.matchRejected,
    userApi.endpoints.deleteAccountAvatar.matchRejected,
    userApi.endpoints.updateProfile.matchRejected,
    userApi.endpoints.updateMfa.matchRejected,
    userApi.endpoints.updateAccountProfile.matchRejected
  ),
  effect: (({ type, payload }, { dispatch }) => {
    const response = isApiResponse(payload) ? payload : undefined;
    handleQueryError(payload, dispatch, type, response?.data || 'Unable to save account information');
  }) satisfies Effect,
});

userApiListener.startListening({
  matcher: userApi.endpoints.getAccount.matchFulfilled,
  effect: ({ payload: account }) => {
    if (account.id === null) {
      Sentry.setUser(null);
      monitoring.clearUser();
      // No `analytics.reset()`: a visitor without an account would get a new device id on every
      // page load. Signing out resets it, in `keycloak.logout`.
    } else {
      // A shared browser never holds another person's unsaved work past the next sign-in, and
      // nothing kept through a forced logout outlives its day, even if no page reads it again.
      removeKeptCopiesNotOwnedBy(account.id);
      removeExpiredKeptCopies();

      Sentry.setUser({
        id: `AID:${account.id?.toString()}`,
        ...(isProduction() ? {} : { email: account.email }),
      });
      monitoring.setUser({
        id: account.id?.toString(),
        // Boolean flag (never the email) so internal traffic can be excluded from RUM/Logs
        // analytics — safe in prod, where account emails are intentionally not forwarded.
        isStaff: isStaffEmail(account.email),
        ...(isProduction() ? {} : { email: account.email }),
      });
      // The raw account id; dbt hashes it before Mixpanel sees it.
      analytics.identify(account.id.toString());
      if (isE2ESession()) {
        Sentry.setTag('isE2E', 'true');
        monitoring.setUserProperty('isE2E', true);
      }
    }
  },
});

userApiListener.startListening({
  matcher: userApi.endpoints.updateProfile.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(userApi.util.invalidateTags(['ACCOUNT']));
    dispatch(addAppSuccessMessage('Your information has been saved'));

    dispatch(push(AppRoutes.Account));
  },
});

userApiListener.startListening({
  matcher: userApi.endpoints.updateMfa.matchFulfilled,
  effect: ({ meta }, { dispatch }) => {
    if (!meta.arg.originalArgs.mfaEnabled) {
      dispatch(addAppSuccessMessage('Your information has been saved'));
    }
  },
});

userApiListener.startListening({
  matcher: userApi.endpoints.checkIdp.matchRejected,
  effect: ({ type, payload }, { dispatch }) => {
    const response = isApiResponse(payload) ? payload : undefined;
    handleQueryError(payload, dispatch, type, response?.data || 'Unable to validate email');
  },
});

userApiListener.startListening({
  matcher: userApi.endpoints.updateAccountProfile.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(addAppSuccessMessage('Your information has been saved'));
  },
});
